import React, { useMemo, useState } from 'react'
import {
  Box,
  Card,
  CardContent,
  Chip,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  styled,
} from '@mui/material'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'

import { CopyValue } from '../CopyValue'
import { cardSx, sectionTitleSx, subtleLabelSx, tableHeadCellSx } from '../styles'
import {
  FINBANK_INSTITUTIONS,
  IMastercardTestProfile,
  MASTERCARD_TEST_DATA_URL,
  MASTERCARD_TEST_PROFILES,
  TESTING_GUIDE_URL,
} from './profiles'

const matches = (profile: IMastercardTestProfile, term: string) =>
  [profile.username, profile.profile, profile.scenario, profile.note]
    .filter(Boolean)
    .some(field => field!.toLowerCase().includes(term))

export const MastercardProfiles: React.FC = () => {
  const [search, setSearch] = useState('')

  const term = search.trim().toLowerCase()
  const shown = useMemo(
    () =>
      term ? MASTERCARD_TEST_PROFILES.filter(p => matches(p, term)) : MASTERCARD_TEST_PROFILES,
    [term]
  )

  return (
    <Card variant="outlined" sx={cardSx}>
      <CardContent sx={{ p: 2.5 }}>
        <Typography variant="subtitle1" sx={sectionTitleSx}>
          Mastercard test profiles
        </Typography>
        <Typography variant="body2" sx={{ ...subtleLabelSx, mt: 0.5 }}>
          Search for <Bold>FinBank</Bold> as the financial institution when connecting a bank, then
          sign in with one of the profiles below. They are Mastercard&apos;s own sandbox fixtures —
          they hold no real customer&apos;s data, and they only work against the sandbox.
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 1.5 }}>
          <Typography variant="caption" sx={subtleLabelSx}>
            Mock banks:
          </Typography>
          {FINBANK_INSTITUTIONS.map(bank => (
            <Chip
              key={bank.id}
              size="small"
              label={`${bank.name} · ${bank.id}`}
              sx={{ fontSize: 11, backgroundColor: '#f5f2f7', color: '#4b2b5c' }}
            />
          ))}
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            flexWrap: 'wrap',
            mt: 2.5,
            mb: 1.5,
          }}
        >
          <TextField
            size="small"
            placeholder="Filter by profile, purpose or scenario"
            value={search}
            onChange={event => setSearch(event.target.value)}
            sx={{ width: 320, '& .MuiInputBase-input': { fontSize: 13.5 } }}
          />
          <Typography variant="caption" sx={subtleLabelSx}>
            {shown.length} of {MASTERCARD_TEST_PROFILES.length} profiles · every password matches
            its username
          </Typography>
        </Box>

        <TableContainer sx={{ border: '1px solid #e0d7e4', borderRadius: 2, maxHeight: 520 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={tableHeadCellSx}>Username</TableCell>
                <TableCell sx={tableHeadCellSx}>Password</TableCell>
                <TableCell sx={tableHeadCellSx}>Beforepay profile</TableCell>
                <TableCell sx={tableHeadCellSx}>Scenario</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {shown.map(entry => (
                <TableRow key={entry.username} hover>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <CopyValue value={entry.username} />
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <CopyValue value={entry.password} />
                  </TableCell>
                  <TableCell sx={{ fontSize: 13, color: '#4b2b5c', minWidth: 170 }}>
                    {entry.profile ?? <Dash>—</Dash>}
                  </TableCell>
                  <TableCell sx={{ fontSize: 13, color: '#4b2b5c', minWidth: 300 }}>
                    {entry.scenario}
                    {entry.note && <Note>{entry.note}</Note>}
                  </TableCell>
                </TableRow>
              ))}
              {shown.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={4}
                    sx={{ ...subtleLabelSx, fontSize: 13, py: 3 }}
                    align="center"
                  >
                    No profile matches &quot;{search.trim()}&quot;.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Box sx={{ mt: 2.5, display: 'flex', gap: 3, flexWrap: 'wrap' }}>
          <DocLink href={MASTERCARD_TEST_DATA_URL}>Mastercard test data documentation</DocLink>
          <DocLink href={TESTING_GUIDE_URL}>Beforepay testing &amp; validation guide</DocLink>
        </Box>
        <Typography variant="body2" sx={{ ...subtleLabelSx, mt: 0.5 }}>
          Mastercard refresh the FinBank data daily, so treat the scenarios above as a guide rather
          than a guarantee.
        </Typography>
      </CardContent>
    </Card>
  )
}

const DocLink: React.FC<{ href: string; children: React.ReactNode }> = ({ href, children }) => (
  <Link
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 0.5,
      fontSize: 13.5,
      fontWeight: 600,
      color: '#2c063f',
      textDecorationColor: '#cac0cf',
    }}
  >
    {children}
    <OpenInNewIcon sx={{ fontSize: 14 }} />
  </Link>
)

const Bold = styled('span')({
  color: '#2c063f',
  fontWeight: 700,
})

const Dash = styled('span')({
  color: '#cac0cf',
})

// Reasons not to reach for a profile earn their own line, so they are not lost in the scenario.
const Note = styled('span')({
  display: 'block',
  marginTop: 2,
  color: '#b23b2e',
  fontSize: 12,
})
